<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import type { Template, TemplateCategory, TemplateSubCategory } from "@/types";
import TemplateEditor from "./TemplateEditor.vue";
import { templatesKey } from "@/logic/provide";

const props = defineProps<{
  category: TemplateCategory;
  canEdit: boolean;
  canAdd: boolean;
  panelX?: number;
}>();

const emit = defineEmits<{
  "click:select": [Template];
}>();

const selectedTemplate = ref<string | null>(null);
const templates = inject(templatesKey);
const panelX = computed(() => props.panelX || 0);

const current_category_templates = computed(() => {
  if (!templates?.value.categories) {
    return null;
  }

  if (
    props.category === "Patient" ||
    props.category === "Praticiens & Centres"
  ) {
    const current =
      templates?.value.categories[props.category] ||
      ({} as Record<string, string[]>);
    const admin =
      templates?.value.categories["Administratif"] ||
      ({} as Record<string, string[]>);
    const admin_keys = Object.keys(admin) as Array<keyof typeof admin>;
    const adminTemplates = admin_keys.map((key) => admin[key]).flat();

    return { ...current, ...{ Administratif: adminTemplates } };
  } else {
    return templates?.value.categories[props.category] || null;
  }
});

const hintTitle = computed(() => {
  if (props.category === "Patient") {
    return "Liste des templates dont le destinataire est un patient";
  } else if (props.category === "Praticiens & Centres") {
    return "Liste des templates dont le destinataire est un praticien ou un centre";
  } else {
    return "Liste des templates pour les autres cas";
  }
});

function templateTitle(id: string) {
  const found = templates?.value.index.get(id);
  return (found !== undefined && templates?.value.list[found].title) || "";
}

function selectTemplate(id: string, use?: boolean) {
  const use_mode = use || false;
  if (use_mode) {
    const found = templates?.value.index.get(id);
    if (!templates || found === undefined) {
      console.error("TemplateList: Cannot find selected template");
      selectedTemplate.value = null;
      return;
    }
    emit("click:select", templates.value.list[found]);
    selectedTemplate.value = null;
  } else {
    selectedTemplate.value = id;
  }
}

function deselectTemplate() {
  selectedTemplate.value = null;
}

function createTemplate() {
  selectedTemplate.value = ""; // Empty string means create a new template (sentinel value)
}

// Autoclose the editor when the category changes
watch(
  props,
  (props) => {
    if (props) {
      deselectTemplate();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="filler" :style="{ 'padding-top': panelX + 'px' }"></div>
  <div class="editor" v-if="selectedTemplate !== null">
    <TemplateEditor
      :template="selectedTemplate"
      :canEdit="props.canEdit"
      :editMode="selectedTemplate === '' ? 'create' : 'edit'"
      @click:cancel="deselectTemplate"
      @click:select="selectTemplate(selectedTemplate, true)"
    />
  </div>
  <div class="templates" v-else>
    <div class="categories" v-if="current_category_templates">
      <template v-if="canAdd">
        <div class="spacer">
          <h3>{{ hintTitle }}</h3>
          <span
            >Vous pouvez ajouter ou modifier les templates pour les courriers ou
            emails ici. Les templates sont sélectionnées lors de l'ajout d'un
            échange à un dossier.</span
          >
        </div>
        <div class="spacer">
          <span class="button" @click.stop="createTemplate">
            Ajouter une nouvelle template
          </span>
        </div>
        <div class="spacer"></div>
      </template>
      <div class="title" v-else>
        <h2>Choix de template</h2>
      </div>
      <div
        class="category"
        v-for="cat of Object.keys(current_category_templates).sort() as TemplateSubCategory[]"
        :key="cat"
      >
        <h3>{{ cat }}</h3>
        <div
          class="template-container"
          v-for="templateId of current_category_templates[cat] || []"
          :key="templateId"
        >
          <div class="template-entry">
            <h4>{{ templateTitle(templateId) }}</h4>
            <template v-if="canEdit">
              <span class="button" @click="selectTemplate(templateId)"
                >Editer</span
              >
            </template>
            <template v-else>
              <span class="button" @click="selectTemplate(templateId, false)"
                >Aperçu</span
              >
              <span class="button" @click="selectTemplate(templateId, true)"
                >Sélectionner</span
              >
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="padding"></div>
</template>

<style scoped lang="scss">
.templates {
  display: flex;
  flex-direction: row;
  gap: 1em;
  justify-content: space-between;
  min-height: calc(100vh - var(--header-height));

  .categories {
    margin-left: 1em;

    .spacer {
      margin-top: 3em;
    }

    .category {
      margin-top: 1em;

      &:last-child {
        padding-bottom: 2em;
      }
    }
  }

  .title {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;
    width: 100%;
    border-bottom-width: 6px;
    border-bottom-style: solid;
    border-color: inherit;
    margin-bottom: 1em;

    h2 {
      margin-left: 0.5em;
      color: inherit;
    }
  }

  .template-entry {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 0.5em;
  }

  .editor {
    position: relative;
    background-color: white;
    width: 100%;
  }
}
.padding {
  padding-bottom: 100px;
}
</style>
